// dependencies
import { addDays } from "date-fns";
import Cookies from "js-cookie";
import React, { ReactNode, useState } from "react";

import {
	IconDefinition,
	faCheckCircle,
	faExclamationCircle,
	faExclamationTriangle,
	faTimesCircle,
} from "@fortawesome/pro-solid-svg-icons";

// components
import Button from "../Button";
import Container from "../Container";
import Icon from "../Icon/Icon";
import Text from "../Text";
// styles
import css from "./PageBanner.module.scss";

interface BaseProps {
	/**
	 * The type of banner to display. This will determine the icon used in the
	 * banner and the color of the banner.
	 */
	type: "success" | "warning" | "error" | "info";
	/**
	 * The size of the banner.
	 */
	size?: "medium" | "large";
	/**
	 * The title of the banner.
	 * there is a default typography applied, so If you need another font size/weight, you should pass in a . react node with the proper typography
	 */
	title: string | ReactNode;
	/**
	 * The description of the banner.
	 * there is a default typography applied, so If you need another font size/weight, you should pass in a . react node with the proper typography
	 */
	description?: string | ReactNode;
	/**
	 * The icon to display in the banner. If not provided, the banner will use
	 * the default icon for the type.
	 */
	icon?: IconDefinition;
	/**
	 * If true, this will render a dismiss button that will hide the banner
	 * when clicked.
	 * @default true
	 */
	isDismissible?: boolean;
	/**
	 * Stores the dismiss state of the banner in a cookie. This is useful if
	 * you don't want to show the banner again after the user has dismissed it.
	 */
	cookieName?: string;
	/**
	 * callback function that will be called when the banner is dismissed.
	 */
	onClose?: () => void;
	/**
	 * The number of days to store the cookie for.
	 * @default 365
	 */
	cookieExpirationInDays?: number;
	/**
	 * The text to display on the action button.
	 */
	actionButtonText?: string;
	/**
	 * The href for the action button.
	 */
	actionButtonHref?: string;
	/**
	 *
	 * callback function for when action button is clicked
	 */
	onActionButtonClick?: () => void;

	/**
	 * a wrapper class name
	 */
	className?: string;

	/**
	 * wrapper style
	 */
	style?: React.CSSProperties;
}

const getIsBannerVisible = (cookieName: string | undefined, isDismissible: boolean) => {
	if (!cookieName || !isDismissible) {
		return true;
	}

	const cookieValue = Cookies.get(`py-banner-${cookieName}`);

	if (!cookieValue) {
		return true;
	}

	return cookieValue !== "hidden";
};

const setCookie = (cookieName: string, cookieValue: "hidden", cookieExpirationDate: Date) => {
	Cookies.set(cookieName, cookieValue, { expires: cookieExpirationDate });
};

const iconLookup = {
	warning: faExclamationTriangle,
	info: faExclamationTriangle,
	error: faExclamationCircle,
	success: faCheckCircle,
};

/**
 * @deprecated use py-components instead
 * A banner to display information, success, warning, or error messages. A
 * common use case is to display a dismissible banner that we don't want to
 * show again after the user has dismissed it.
 */
const PageBanner = (props: BaseProps) => {
	const {
		type,
		size = "medium",
		title,
		description,
		cookieName,
		cookieExpirationInDays = 365,
		isDismissible = true,
		onClose = () => {},
		actionButtonText,
		actionButtonHref,
		onActionButtonClick = () => {},
		className = "",
		style = {},
		icon,
	} = props;

	const colors = {
		warning: "yellow",
		error: "red",
		success: "green",
		info: "blue",
	};

	const [isBannerVisible, setIsBannerVisible] = useState(getIsBannerVisible(cookieName, isDismissible));

	const handleDismiss = (cookieName: string, cookieExpirationInDays: number, onClose: () => void) => {
		if (cookieName) {
			const cookieExpirationDate = addDays(new Date(), cookieExpirationInDays);

			// prepend `py-banner-` to the cookie name to avoid conflicts with
			// other cookies.
			setCookie(`py-banner-${cookieName}`, "hidden", cookieExpirationDate);
		}

		// hide the banner
		setIsBannerVisible(false);

		// call the onClose callback
		onClose();
	};
	const chosenIcon = icon || iconLookup[type];
	return (
		<div
			style={style}
			className={` ${css.banner} ${isDismissible ? css.dismissibleBanner : css.nonDismissibleBanner} ${css[type]} ${css[size]} ${className} ${css.fullWidth} `}
			hidden={!isBannerVisible}
		>
			<Icon
				testid={`banner-icon-${chosenIcon?.iconName}`}
				size={size === "large" ? "regular" : "small"}
				icon={chosenIcon}
				className={css.bannerIcon}
			/>
			<Container flex={1} testId="banner-children" gap="8px" horizontalAlignment="space-between">
				<div>
					<Text size={size === "large" ? "paragraph-2" : "caption"} weight="semi-bold">
						{title}
					</Text>
					{description ? (
						<Text size={size === "large" ? "paragraph-2" : "caption"} weight="regular">
							{description}
						</Text>
					) : null}
					{actionButtonText && type !== "warning" ? (
						<Button
							className={css.actionButton}
							href={actionButtonHref || ""}
							onClick={onActionButtonClick}
							color={colors[type] as "blue" | "green" | "red"}
						>
							{actionButtonText}
						</Button>
					) : null}
				</div>
				{isDismissible ? (
					<button
						onClick={() => {
							cookieName ? handleDismiss(cookieName, cookieExpirationInDays, onClose) : onClose();
						}}
						data-testid="banner-dismiss-button"
						className={css.dismissButton}
					>
						<Icon
							className={css.dismissButton}
							size={size === "large" ? "regular" : "x-small"}
							data-testid="banner-dismiss-button"
							icon={faTimesCircle}
						/>
					</button>
				) : null}
			</Container>
		</div>
	);
};

export default PageBanner;
